import { useQuery } from "@tanstack/react-query";
import {
  PlayOnePagerHeader,
  PlayOnePagerNotice,
  type PlayOnePagerCover,
  type PlayOnePagerLink,
} from "@softmax/product-ux/play";
import { buildHref } from "@softmax/web-addresses";
import { FeatureLink } from "@softmax/web-features/navigation";
import { useDiscardUnavailableData } from "@softmax/web-features/data";
import { gamePresentationOf } from "@softmax/web-features/coworlds";
import { monogramOf } from "@/features/preview/media";
import type { PublicPlayShell } from "../lib/types";
import { type OnePagerCoworld } from "./coworlds";
import { LiveClock } from "./live-clock";
import { decorationOptions } from "./one-pager.queries";
import type { LiveState } from "./types";
export function OnePagerHero({
  coworld,
  shell,
  corpus,
  href,
  viewerId,
  live,
}: {
  readonly coworld: OnePagerCoworld;
  readonly shell: PublicPlayShell;
  readonly corpus: string;
  readonly href: string;
  readonly viewerId: string | null;
  readonly live: LiveState | undefined;
}) {
  const options = decorationOptions({
    href,
    viewerId,
  });
  const decoration = useQuery(options);
  const unavailable = useDiscardUnavailableData({
    queryKey: options.queryKey,
    error: decoration.error,
  });
  const decorationData = unavailable ? undefined : decoration.data;
  const game = shell.league.route.game;
  const name = coworld.name;
  const links: PlayOnePagerLink[] = coworld.links.map((link) => {
    if (link.kind === "external")
      return { label: link.label, href: link.href, external: true };

    return {
      label: link.label,
      href:
        link.destination.name === "docs"
          ? buildHref(link.destination)
          : buildHref({ ...link.destination, game }),
      external: false,
    };
  });
  links.push({
    label: "Store page",
    href:
      gamePresentationOf(corpus).home === "store"
        ? buildHref({
            name: "game.home",
            game,
            search: new URLSearchParams({ store: "1" }),
          })
        : buildHref({ name: "game.preview", game }),
    external: false,
  });
  let cover: PlayOnePagerCover | null = null;

  if (coworld.art.cover) {
    const { file, focal, focalPhone } = coworld.art.cover;
    const base = "/home/headers/" + file;
    cover = {
      src: base + "-wide.webp",
      focal,
      focalPhone,
      sources: ["avif", "webp"].flatMap((format) => [
        {
          media: "(max-width: 760px)",
          type: "image/" + format,
          srcSet: `${base}-16x9-1x.${format} 960w, ${base}-16x9.${format} 1920w`,
          sizes: "200vw",
        },
        {
          type: "image/" + format,
          srcSet: `${base}-wide-1x.${format} 1440w, ${base}-wide.${format} 2880w`,
          sizes: "(max-width: 1280px) 100vw, 1220px",
        },
      ]),
    };
  } else if (coworld.art.still) {
    cover = {
      src: coworld.art.still,
      focal: "50% 50%",
      focalPhone: "50% 50%",
      sources: [],
    };
  }

  return (
    <PlayOnePagerHeader
      accent={coworld.art.accent}
      name={name}
      leagueNote={shell.league.name !== name ? shell.league.name : null}
      pitch={
        coworld.pitch ??
        decorationData?.blurb ??
        `Compete in ${name} on Softmax.`
      }
      logo={decorationData?.icon ?? coworld.art.logo ?? null}
      monogram={coworld.art.monogram ?? monogramOf(name)}
      cover={cover}
      site={coworld.site ?? null}
      links={links}
      renderLink={(props) => <FeatureLink {...props} />}
      notice={
        decoration.isError ? (
          <PlayOnePagerNotice
            state={{
              kind: "error",
              message: "Store details unavailable.",
              pending: decoration.isFetching,
              onRetry: () => void decoration.refetch(),
            }}
          />
        ) : null
      }
      live={live ? <LiveClock live={live} /> : null}
    />
  );
}
